import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { UploadCloud, Trash2, Music2, Wand2, Hourglass, Pencil, Check, SlidersHorizontal } from "lucide-react";
import { api, trackCoverUrl, eur, fmtError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";
import { StudioSales } from "../components/StudioSales";
import { AdminArtists } from "../components/AdminArtists";
import { ArtistProfile } from "../components/ArtistProfile";
import { StudioPayouts } from "../components/StudioPayouts";
import { StudioContract } from "../components/StudioContract";
import { AdminClients } from "../components/AdminClients";
import { AdminDashboard } from "../components/AdminDashboard";
import { AdminReviews } from "../components/AdminReviews";
import { AdminNotifications } from "../components/AdminNotifications";
import { StudioPromo } from "../components/StudioPromo";
import { StudioPostKit } from "../components/StudioPostKit";
import { SiteBanners } from "../components/SiteBanners";
import { AdminMeta } from "../components/AdminMeta";
import { AdminEmail } from "../components/AdminEmail";
import { AdminContent } from "../components/AdminContent";
import { AdminBroadcast } from "../components/AdminBroadcast";

const USAGE_OPTIONS = ["Pub", "Film", "Artiste"];

const ADMIN_SECTIONS = [
  { id: "studio-dashboard", label: "Tableau de bord" },
  { id: "studio-promo", label: "Visuels promo" },
  { id: "studio-postkit", label: "Kit post" },
  { id: "studio-content", label: "Contenu auto" },
  { id: "studio-broadcast", label: "Envois groupés" },
  { id: "studio-meta", label: "Publication auto" },
  { id: "studio-email", label: "Email du site" },
  { id: "studio-banners", label: "Bannières" },
  { id: "studio-sales", label: "Ventes" },
  { id: "studio-payouts", label: "Reversements" },
  { id: "studio-clients", label: "Clients" },
  { id: "studio-reviews", label: "Avis" },
  { id: "studio-artists", label: "Artistes" },
];

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -110 });
  else el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Admin = () => {
  const { user, checked, setUser } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", price: "", exclusive_price: "", bpm: "", usages: [], genres: "", moods: "" });
  const [audio, setAudio] = useState(null);
  const [cover, setCover] = useState(null);
  const [busy, setBusy] = useState(false);
  const [coverBusy, setCoverBusy] = useState("");
  const [imgVersion, setImgVersion] = useState(0);
  const [priceEdit, setPriceEdit] = useState({ id: null, value: "" });
  const [fullEdit, setFullEdit] = useState(null);

  const openFullEdit = (t) =>
    setFullEdit({
      id: t.track_id, title: t.title, description: t.description || "",
      price: String(t.price), exclusive_price: t.exclusive_price ? String(t.exclusive_price) : "",
      bpm: String(t.bpm || ""), genres: (t.genres || []).join(", "),
      moods: (t.moods || []).join(", "), usages: [...(t.usages || [])],
    });

  const saveFullEdit = async (t) => {
    try {
      await api.patch(`/studio/tracks/${t.track_id}`, {
        title: fullEdit.title, description: fullEdit.description,
        price: parseFloat(fullEdit.price), bpm: parseInt(fullEdit.bpm) || 0,
        exclusive_price: parseFloat(fullEdit.exclusive_price) || 0,
        genres: fullEdit.genres.split(","), moods: fullEdit.moods.split(","),
        usages: fullEdit.usages,
      });
      toast.success(`« ${fullEdit.title} » mis à jour`);
      setFullEdit(null);
      load();
    } catch (e) {
      toast.error(fmtError(e));
    }
  };

  const toggleEditUsage = (u) =>
    setFullEdit((f) => ({ ...f, usages: f.usages.includes(u) ? f.usages.filter((x) => x !== u) : [...f.usages, u] }));

  const load = () => api.get("/studio/tracks").then((r) => setTracks(r.data)).catch(() => {});
  useEffect(() => {
    if (user && ["admin", "artist"].includes(user.role)) load();
  }, [user]);

  if (checked && (user === false || !["admin", "artist"].includes(user?.role))) return <Navigate to="/" replace />;
  if (!user) return null;

  const isAdmin = user.role === "admin";
  const isPending = user.role === "artist" && user.artist_status !== "approved";

  const toggleUsage = (u) =>
    setForm((f) => ({ ...f, usages: f.usages.includes(u) ? f.usages.filter((x) => x !== u) : [...f.usages, u] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!audio) {
      toast.error("Ajoutez un fichier audio");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("description", form.description);
      fd.append("price", form.price);
      fd.append("exclusive_price", form.exclusive_price || 0);
      fd.append("bpm", form.bpm || 0);
      fd.append("usages", form.usages.join(","));
      fd.append("genres", form.genres);
      fd.append("moods", form.moods);
      fd.append("audio", audio);
      if (cover) fd.append("cover", cover);
      await api.post("/admin/tracks", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`« ${form.title} » publié au catalogue`);
      setForm({ title: "", description: "", price: "", exclusive_price: "", bpm: "", usages: [], genres: "", moods: "" });
      setAudio(null);
      setCover(null);
      e.target.reset();
      load();
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setBusy(false);
    }
  };

  const savePrice = async (t) => {
    const val = parseFloat(priceEdit.value);
    if (!val || val <= 0) {
      toast.error("Prix invalide");
      return;
    }
    try {
      await api.patch(`/studio/tracks/${t.track_id}`, { price: val });
      toast.success(`Prix de « ${t.title} » mis à jour : ${val} €`);
      setPriceEdit({ id: null, value: "" });
      load();
    } catch (e) {
      toast.error(fmtError(e));
    }
  };

  const genCover = async (t) => {
    setCoverBusy(t.track_id);
    try {
      await api.post(`/admin/tracks/${t.track_id}/cover/generate`);
      toast.success(`Jaquette IA créée pour « ${t.title} »`);
      setImgVersion(Date.now());
      load();
    } catch (e) {
      toast.error(fmtError(e));
    } finally {
      setCoverBusy("");
    }
  };

  const removeTrack = async (t) => {    try {
      await api.delete(`/admin/tracks/${t.track_id}`);
      toast.success(`« ${t.title} » retiré du catalogue`);
      load();
    } catch (e) {
      toast.error(fmtError(e));
    }
  };

  const inputCls =
    "w-full border border-white/15 bg-transparent px-4 py-3 text-sm outline-none transition-colors focus:border-amber-500";
  const labelCls = "mb-2 block text-[10px] uppercase tracking-[0.25em] text-zinc-500";

  return (
    <main data-testid="admin-page" className="mx-auto max-w-7xl px-5 pb-24 pt-32 sm:px-8">
      <Reveal>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-amber-500">
              {isAdmin ? "Studio — Administration" : `Studio de ${user.artist_name || user.name}`}
            </p>
            <h1 className="mt-3 font-display text-4xl font-black tracking-tighter sm:text-5xl">Publier un morceau</h1>
          </div>
          {isAdmin && <AdminNotifications />}
        </div>
      </Reveal>

      {isAdmin && (
        <nav data-testid="admin-sections-nav" className="mt-8 flex flex-wrap gap-2">
          {ADMIN_SECTIONS.map((s) => (
            <button
              key={s.id} type="button" data-testid={`nav-${s.id}`}
              onClick={() => scrollToSection(s.id)}
              className="border border-white/15 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 transition-colors hover:border-amber-500 hover:text-amber-400"
            >
              {s.label}
            </button>
          ))}
        </nav>
      )}

      {isPending && (
        <div className="mt-8 flex items-start gap-3 border border-amber-500/40 bg-amber-500/10 p-5" data-testid="artist-pending-banner">
          <Hourglass className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
          <p className="text-sm leading-relaxed text-amber-200/90">
            Votre compte artiste est en attente de validation par le studio. Vous pourrez publier
            vos morceaux dès que votre inscription sera confirmée.
          </p>
        </div>
      )}

      <div className={`mt-12 grid grid-cols-1 gap-10 lg:grid-cols-2 ${isPending ? "pointer-events-none opacity-40" : ""}`}>
        <Reveal>
          <form onSubmit={submit} className="space-y-5 border border-white/[0.08] bg-surface p-8" data-testid="admin-upload-form">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
              <div>
                <label className={labelCls}>Titre</label>
                <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-title" className={inputCls} placeholder="Nom du morceau" />
              </div>
              <div>
                <label className={labelCls}>Prix de la licence (€)</label>
                <input required type="number" min="1" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="admin-price" className={inputCls} placeholder="79" />
              </div>
              <div>
                <label className={labelCls}>Prix exclusivité (€, optionnel)</label>
                <input type="number" min="0" step="0.01" value={form.exclusive_price} onChange={(e) => setForm({ ...form, exclusive_price: e.target.value })} data-testid="admin-exclusive-price" className={inputCls} placeholder="Ex. 499 — vide = non proposée" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Description</label>
              <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="admin-description" className={inputCls} placeholder="Ambiance, usage recommandé…" />
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
              <div>
                <label className={labelCls}>BPM</label>
                <input type="number" min="0" value={form.bpm} onChange={(e) => setForm({ ...form, bpm: e.target.value })} data-testid="admin-bpm" className={inputCls} placeholder="90" />
              </div>
              <div>
                <label className={labelCls}>Genres (virgules)</label>
                <input value={form.genres} onChange={(e) => setForm({ ...form, genres: e.target.value })} data-testid="admin-genres" className={inputCls} placeholder="Cinématique, Ambient" />
              </div>
              <div>
                <label className={labelCls}>Humeurs (virgules)</label>
                <input value={form.moods} onChange={(e) => setForm({ ...form, moods: e.target.value })} data-testid="admin-moods" className={inputCls} placeholder="Sombre, Tendu" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Destiné à</label>
              <div className="flex gap-2" data-testid="admin-usages">
                {USAGE_OPTIONS.map((u) => (
                  <button
                    type="button"
                    key={u}
                    onClick={() => toggleUsage(u)}
                    data-testid={`admin-usage-${u.toLowerCase()}`}
                    className={`border px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors ${
                      form.usages.includes(u) ? "border-amber-500 bg-amber-500 text-black" : "border-white/15 text-zinc-400 hover:border-amber-500/60"
                    }`}
                  >
                    {u}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Fichier audio (mp3 / wav)</label>
                <input required type="file" accept="audio/*" onChange={(e) => setAudio(e.target.files[0])} data-testid="admin-audio-file" className="w-full text-xs text-zinc-400 file:mr-4 file:border file:border-amber-500/50 file:bg-transparent file:px-4 file:py-2.5 file:text-xs file:uppercase file:tracking-[0.2em] file:text-amber-400 hover:file:bg-amber-500 hover:file:text-black" />
              </div>
              <div>
                <label className={labelCls}>Jaquette (vide = logo du site)</label>
                <input type="file" accept="image/*" onChange={(e) => setCover(e.target.files[0])} data-testid="admin-cover-file" className="w-full text-xs text-zinc-400 file:mr-4 file:border file:border-amber-500/50 file:bg-transparent file:px-4 file:py-2.5 file:text-xs file:uppercase file:tracking-[0.2em] file:text-amber-400 hover:file:bg-amber-500 hover:file:text-black" />
              </div>
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="admin-submit"
              className="flex w-full items-center justify-center gap-3 bg-amber-500 py-4 text-xs font-bold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-400 disabled:opacity-50"
            >
              <UploadCloud className="h-4 w-4" />
              {busy ? "Envoi en cours…" : "Publier au catalogue"}
            </button>
          </form>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="border border-white/[0.08] bg-surface p-8">
            <h2 className="flex items-center gap-3 font-display text-2xl font-medium">
              <Music2 className="h-5 w-5 text-amber-500" /> Catalogue en ligne
            </h2>
            <div className="mt-6 space-y-3" data-testid="admin-track-list">
              {tracks.length === 0 && (
                <p className="text-sm text-zinc-500">Aucun morceau publié.</p>
              )}
              {tracks.map((t) => (
                <div key={t.track_id} data-testid={`admin-track-${t.track_id}`}>
                  <div className="flex items-center gap-4 border border-white/[0.06] p-3">
                    <img src={`${trackCoverUrl(t)}?v=${imgVersion}`} alt={t.title} className="h-12 w-12 border border-white/10 object-cover" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-display text-sm font-medium">{t.title}</p>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{(t.usages || []).join(" · ")}</p>
                      {t.exclusive_sold ? (
                        <span data-testid={`exclusive-sold-${t.track_id}`} className="mt-1 inline-block border border-red-500/40 px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] text-red-400">
                          Exclusivité vendue — hors catalogue
                        </span>
                      ) : t.exclusive_price > 0 ? (
                        <span data-testid={`exclusive-tag-${t.track_id}`} className="mt-1 inline-block border border-amber-500/40 px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] text-amber-400">
                          Exclu {eur(t.exclusive_price)}
                        </span>
                      ) : null}
                      <p className="mt-0.5 text-[10px] tracking-[0.1em] text-zinc-600" data-testid={`stats-${t.track_id}`}>
                        {t.plays || 0} écoute{(t.plays || 0) > 1 ? "s" : ""} · {t.sales_count || 0} vente{(t.sales_count || 0) > 1 ? "s" : ""}
                      </p>
                    </div>
                    {priceEdit.id === t.track_id ? (
                      <span className="flex items-center gap-1">
                        <input
                          type="number"
                          min="1"
                          step="0.01"
                          value={priceEdit.value}
                          onChange={(e) => setPriceEdit({ id: t.track_id, value: e.target.value })}
                          data-testid={`price-input-${t.track_id}`}
                          className="w-20 border border-amber-500/60 bg-transparent px-2 py-1 text-xs outline-none"
                          autoFocus
                        />
                        <button
                          onClick={() => savePrice(t)}
                          data-testid={`price-save-${t.track_id}`}
                          className="flex h-7 w-7 items-center justify-center text-emerald-400 hover:text-emerald-300"
                          title="Enregistrer le prix"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                      </span>
                    ) : (
                      <button
                        onClick={() => setPriceEdit({ id: t.track_id, value: String(t.price) })}
                        data-testid={`price-edit-${t.track_id}`}
                        className="flex items-center gap-1.5 text-xs font-bold text-amber-400 transition-colors hover:text-amber-300"
                        title="Modifier le prix"
                      >
                        {eur(t.price)} <Pencil className="h-3 w-3 text-zinc-500" />
                      </button>
                    )}
                    <button
                      onClick={() => (fullEdit?.id === t.track_id ? setFullEdit(null) : openFullEdit(t))}
                      data-testid={`edit-track-${t.track_id}`}
                      className="flex h-9 w-9 items-center justify-center text-zinc-500 transition-colors hover:text-amber-400"
                      title="Modifier titre, descriptif, tempo…"
                    >
                      <SlidersHorizontal className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => genCover(t)}
                      disabled={coverBusy === t.track_id}
                      data-testid={`admin-gencover-${t.track_id}`}
                      className="flex h-9 w-9 items-center justify-center text-zinc-500 transition-colors hover:text-amber-400 disabled:opacity-40"
                      title="Générer une jaquette IA unique"
                    >
                      <Wand2 className={`h-4 w-4 ${coverBusy === t.track_id ? "animate-spin" : ""}`} />
                    </button>
                    <button
                      onClick={() => removeTrack(t)}
                      data-testid={`admin-delete-${t.track_id}`}
                      className="flex h-9 w-9 items-center justify-center text-zinc-500 transition-colors hover:text-red-400"
                      title="Retirer du catalogue"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  {fullEdit?.id === t.track_id && (
                    <div className="space-y-3 border border-amber-500/30 bg-amber-500/5 p-4" data-testid={`edit-form-${t.track_id}`}>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <input value={fullEdit.title} onChange={(e) => setFullEdit({ ...fullEdit, title: e.target.value })} data-testid={`edit-title-${t.track_id}`} className={inputCls} placeholder="Titre" />
                        <input type="number" min="1" step="0.01" value={fullEdit.price} onChange={(e) => setFullEdit({ ...fullEdit, price: e.target.value })} data-testid={`edit-price-${t.track_id}`} className={inputCls} placeholder="Prix licence (€)" />
                        <input type="number" min="0" step="0.01" value={fullEdit.exclusive_price} onChange={(e) => setFullEdit({ ...fullEdit, exclusive_price: e.target.value })} data-testid={`edit-exclusive-price-${t.track_id}`} className={inputCls} placeholder="Prix exclusivité (€, 0 = non)" />
                      </div>
                      <textarea rows={2} value={fullEdit.description} onChange={(e) => setFullEdit({ ...fullEdit, description: e.target.value })} data-testid={`edit-desc-${t.track_id}`} className={inputCls} placeholder="Description" />
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <input type="number" min="0" value={fullEdit.bpm} onChange={(e) => setFullEdit({ ...fullEdit, bpm: e.target.value })} data-testid={`edit-bpm-${t.track_id}`} className={inputCls} placeholder="Tempo (BPM)" />
                        <input value={fullEdit.genres} onChange={(e) => setFullEdit({ ...fullEdit, genres: e.target.value })} data-testid={`edit-genres-${t.track_id}`} className={inputCls} placeholder="Genres (virgules)" />
                        <input value={fullEdit.moods} onChange={(e) => setFullEdit({ ...fullEdit, moods: e.target.value })} data-testid={`edit-moods-${t.track_id}`} className={inputCls} placeholder="Humeurs (virgules)" />
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {USAGE_OPTIONS.map((u) => (
                          <button
                            type="button"
                            key={u}
                            onClick={() => toggleEditUsage(u)}
                            data-testid={`edit-usage-${u.toLowerCase()}-${t.track_id}`}
                            className={`border px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] transition-colors ${
                              fullEdit.usages.includes(u) ? "border-amber-500 bg-amber-500 text-black" : "border-white/15 text-zinc-400 hover:border-amber-500/60"
                            }`}
                          >
                            {u}
                          </button>
                        ))}
                        <button
                          onClick={() => saveFullEdit(t)}
                          data-testid={`edit-save-${t.track_id}`}
                          className="ml-auto flex items-center gap-2 bg-amber-500 px-5 py-2 text-[11px] font-bold uppercase tracking-[0.2em] text-black transition-colors hover:bg-amber-400"
                        >
                          <Check className="h-4 w-4" /> Enregistrer
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>

      {!isPending && (
        <div className="mt-10 space-y-10">
          {isAdmin && <div id="studio-dashboard" className="scroll-mt-28"><AdminDashboard /></div>}
          {user.role === "artist" && <StudioContract user={user} onChanged={async () => setUser((await api.get("/auth/me")).data)} />}
          <div id="studio-promo" className="scroll-mt-28"><StudioPromo artistName={user.artist_name || user.name} /></div>
          <div id="studio-postkit" className="scroll-mt-28"><StudioPostKit /></div>
          {isAdmin && <div id="studio-content" className="scroll-mt-28"><AdminContent /></div>}
          {isAdmin && <div id="studio-broadcast" className="scroll-mt-28"><AdminBroadcast /></div>}
          {isAdmin && <div id="studio-meta" className="scroll-mt-28"><AdminMeta /></div>}
          {isAdmin && <div id="studio-email" className="scroll-mt-28"><AdminEmail /></div>}
          {isAdmin && <div id="studio-banners" className="scroll-mt-28"><SiteBanners /></div>}
          <div id="studio-sales" className="scroll-mt-28"><StudioSales isAdmin={isAdmin} /></div>
          <div id="studio-payouts" className="scroll-mt-28"><StudioPayouts isAdmin={isAdmin} /></div>
          {isAdmin && <div id="studio-clients" className="scroll-mt-28"><AdminClients /></div>}
          {isAdmin && <div id="studio-reviews" className="scroll-mt-28"><AdminReviews /></div>}
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
            <ArtistProfile user={user} onSaved={setUser} />
            {isAdmin && <div id="studio-artists" className="scroll-mt-28"><AdminArtists /></div>}
          </div>
        </div>
      )}
    </main>
  );
};

export default Admin;
